<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, getCurrentInstance, watch, useTemplateRef } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification";
import Spinner from '@/components/ui/Spinner.vue';
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconPlus from '@/components/icon/IconPlus.vue';
import IconPlay from '@/components/icon/IconPlay.vue';
import IconWarning from '@/components/icon/IconWarning.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import StreamService from '@/services/StreamService';
import { sortStreams } from '@/utils';
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort';
import StreamCardView from '@/components/ui/StreamCardView.vue';
import AddScheduleModal from '@/components/modals/AddScheduleModal.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import Tooltip from '../../components/Atoms/Tooltip.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import Badge from '../../components/Badge.vue';
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import ViewModeSwitcher from '../../components/ui/ViewModeSwitcher.vue';
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue';
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue';
import Alert from '../../components/Atoms/Alert.vue';
import SidebarAddStream from '../../components/modals/SidebarAddStream.vue';
import PageHeader from '@/components/ui/PageHeader.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import ListingSearchEmpty from '@/components/ui/ListingSearchEmpty.vue';
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue';

const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

//TemplateRefs
const sidebarAddStream = useTemplateRef('sidebarAddStream')
const sortDropdown = useTemplateRef('sortDropdown')

// Refs
const showBanner = ref(false)
const liveCounter = ref(0)
const downgradeDatedifference = ref(0)
const warningDays = ref(3)
const withinLast3Days = ref(false)
const startDisabling = ref(false)
const noAccess = ref(false)
const searchWidgetInput = ref('')
const deleteModalConfiguredStream = ref(null)
const { sortBy, setSort } = usePersistedSort('castr.sort.scheduler', DATE_NAME_SORT, 'Newest')

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const scheduledStreams = computed(() => store.getters['Streams/scheduledStreams'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const activeTeam = computed(() => store.getters['User/activeTeam'])
const userEmail = computed(() => store.getters['User/userEmail'])

const userloaded = computed(() => {
	return store.state.User.userloaded;
})

const memberPermission = computed(() => {
	return activeTeam.value && store.state.User.memberPermission
})

const cannotManageStreams = computed(() => {
	const used = scheduledStreams.value?.length ?? 0
	if (addonSubscriptions.value.length === 0 || (addonSubscriptions.value.find(subscribtion => (subscribtion.category === 'scheduled' && subscribtion.enabled)) && used < maxScheduleNumber.value)) return false
	if (activeTeam.value && !memberPermission.value) return true;
	if (!addonSubscriptions.value.find(subscribtion => (subscribtion.category === 'scheduled')) && used === 0) return false;
	if (used >= maxScheduleNumber.value) return true
	return !memberPermission.value?.preRecordedStreamManage;
})

const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])

// Sort + search only add value once there's a meaningful list to manage.
const showListControls = computed(() => scheduledStreams.value.length > 2)
const sortByLabel = computed(() => sortBy.value)

const streamFilter = computed(() => {
	const filteredStreams = scheduledStreams.value.filter(
		item => {
			const searchStr = searchWidgetInput.value.toLowerCase();
			const result = item.name.toLowerCase().includes(searchStr);
			return result
		}
	)
	return sortStreams(filteredStreams, sortBy.value) || filteredStreams
})

// A query that misses every stream is not the "no streams yet" state: the
// onboarding placeholder below used to take over and hid that the list still
// exists. Tell these apart so the miss explains itself and can be cleared.
const noSearchMatches = computed(() => !!searchWidgetInput.value && streamFilter.value.length === 0)

const streamFilterenabled = computed(() => {
	return scheduledStreams.value.filter(item => item.enabled)
})

const disabledTitle = computed(() => {
	if ((scheduledStreams.value?.length ?? 0) >= maxScheduleNumber.value) return `You have created ${scheduledStreams.value.length} / ${maxScheduleNumber.value} pre-recorded streams. Need more? <a class="text-underline text-white" target="_blank" href="/app/subscribe">Upgrade here</a>`
	if (!activeTeam.value || !memberPermission.value) return `You don’t have permission. Contact the team owner at <a class="text-dark-8 text-small" :href="'mailto:' + ${userEmail.value}">${userEmail.value}</a> to gain access.`
	return
})

const maxScheduleNumber = computed(() => store.getters['User/maxScheduledStreams'])

const scheduleLimit = computed(() => {
	const c = Number(maxScheduleNumber.value)
	return Number.isFinite(c) && c > 0 ? c : null
})

// `used` is this workspace's; the meter pairs it with the account's total, so
// an empty workspace still shows a plan that is being spent elsewhere.
const scheduleHeaderUsage = computed(() => {
	if (loadingStatus.value) return null
	const meta = {
		used: scheduledStreams.value?.length || 0,
		label: 'streams',
		labelSingular: 'stream',
		resource: 'scheduled',
	}
	if (scheduleLimit.value) {
		meta.limit = scheduleLimit.value
		meta.planLabel = 'pre-recorded streams'
	}
	return meta
})

const atScheduledLimit = computed(() => {
	const max = scheduleLimit.value
	if (!max) return false
	return (scheduledStreams.value?.length ?? 0) >= max
})

const canCreateScheduledStream = computed(() =>
	(!cannotManageStreams.value || atScheduledLimit.value) &&
	(!activeTeam.value || !!memberPermission.value?.preRecordedStreamManage)
)

const hasEnabledLinkedSources = (stream) => {
	return stream?.platforms?.some(platform => platform.enabled && platform.linkedSource) || false;
}

const getTooltipLinkedSourceText = (stream) => {
	const enabledLinkedSource = stream?.platforms?.find(platform => platform.enabled && platform.linkedSource);
	if (!enabledLinkedSource || !enabledLinkedSource?.streamObject) return ''
	const linkedStreamName = enabledLinkedSource.streamObject.name
	return `This livestream cannot be deleted because it is being used as a source in ${linkedStreamName} livestream. To delete this livestream, first remove it as a source from ${linkedStreamName} livestream.`
}

// Watchers
watch(userloaded, async () => {
	const permission = await checkPermissions();
	if (!activeTeam.value || permission) {
		noAccess.value = false;
	} else {
		noAccess.value = true;
	}
	store.dispatch('Ui/setLoadingStatus', false);
})

// Hooks
onMounted(async () => {
	store.dispatch('Ui/setLoadingStatus', true);
	await store.dispatch('Streams/getStreams');
	store.dispatch('Ui/setLoadingStatus', false);
	// await this.$store.dispatch('User/getinfoOwner', null, { root: true });
})

// Methods

const checkAllowed = async () => {
	// 1. If have excess stream enabled this function will count excess 
	// and disable those automatically.
	//2. If livestream count is greater than the allowed stream then we will show
	// a warning banner for 3 days.
	//3. After 3 days we will disable the livstream too.
	let list = scheduledStreams.value;
	if (list.length > 0) {
		let check = list.filter(s => {
			return s.enabled;
		});
		let enabledStreamlength = check.length;
		if (!startDisabling.value && (enabledStreamlength > maxScheduleNumber.value)) {
			if (liveCounter.value > maxScheduleNumber.value) {
				// const oneDay = 5 * 60 * 1000; 
				const oneDay = 24 * 60 * 60 * 1000;
				let d = new Date();
				let utc = d.getTime();
				let current = utc;
				let start = new Date('Jul 12 2023 10:00:00 GMT+0000').getTime();
				let dayspassed = Math.floor((current - start) / oneDay);
				if (withinLast3Days.value || ((dayspassed < 3) && (dayspassed >= 0))) {
					if (withinLast3Days.value) {
						warningDays.value = 3 - downgradeDatedifference.value;
					}
					if (dayspassed < 3) {
						warningDays.value = 3 - dayspassed;
					}
					showBanner.value = true;
				} else {
					warningDays.value = 0;
					startDisabling.value = true;
					showBanner.value = false;
					checkAllowed('scheduled');
				}
			} else {
				showBanner.value = false;
			}
		} else {
			showBanner.value = false;
		}

		check.sort((a, b) => a.creationTime - b.creationTime);
		if (enabledStreamlength >= maxScheduleNumber.value) {
			localStorage.setItem('notAllowedscheduled', true);
		} else {
			localStorage.setItem('notAllowedscheduled', false);
		}
	}
}

const checkPermissions = () => {
	return (memberPermission.value?.preRecordedStreamManage || memberPermission.value?.preRecordedStreamToggle || memberPermission.value?.preRecordedStreamPlatformToggle || memberPermission.value?.preRecordedStreamDelete);
}

const showCreateStreamModal = () => {
	if (cannotManageStreams.value) return;
	// showModal("modal-add-schedule");
	sidebarAddStream.value.toggleSidebar()
}

const changeSortBy = (val) => {
	setSort(val)
	sortDropdown.value?.toggleDropdown()
}

const toggleView = (value) => {
	store.dispatch('Ui/setCompactModeStreams', value);
}

const updateStreamPulse = (stream) => {
	// Method to handle media pulse updates from stream cards
	// Currently a no-op for scheduled streams as they don't have live pulse
}

const gotoUpgrade = () => {
	window.location.href = `/app/subscribe?category=live`;
}

const onNewStream = async (stream, regionDetails) => {
	notify({ group: 'success', text: 'Stream deployed successfully' });

	// Redirect immediately for seamless UX
	let redirectPath = '/scheduled/';
	redirectPath += stream._id;
	router.push({ path: redirectPath });

	// Refresh store in background
	store.dispatch('Streams/getStreams', true);
	
	// track event
	window.trackEvent('Stream Deployed', {
		streamId: stream._id,
		streamName: stream.name,
		streamType: stream.type,
		region: regionDetails ? regionDetails.name : undefined,
	});
}

const onStreamDeleteRequest = (stream) => {
	deleteModalConfiguredStream.value = stream;
	showModal('modal-confirm');
}

const onStreamDeleteConfirm = async () => {
	// pop stream out of the list
	const index = scheduledStreams.value.indexOf(deleteModalConfiguredStream.value);

	try {
		await StreamService.deleteStream(deleteModalConfiguredStream.value._id);

		// track event
		const removedStream = deleteModalConfiguredStream.value;
		window.trackEvent('Stream Deleted', {
			streamId: removedStream._id,
			streamName: removedStream.name,
			streamType: removedStream.type,
		});
		// Force refresh to remove deleted stream from list
		await store.dispatch('Streams/getStreams', true);
		notify({ group: 'info', text: 'The stream was deleted' });
	} catch (err) {
		notify({ group: 'error', title: err.error, text: err.message });
		// push stream back to list
		onNewStream(deleteModalConfiguredStream.value);
	}

	deleteModalConfiguredStream.value = null;
}

const showTutorialModal = () => {
	// window.open('https://docs.castr.com/en/articles/2995226-how-to-start-streaming-pre-recorded-videos', '_blank', 'noopener');
	showModal('modal_prerecorded_tutorial');
}

</script>

<template>
	<div class="container detail-shell page-title-area px-4">
		<PageHeader title="Pre-Recorded Streams"
			subtitle="Schedule and loop pre-recorded videos as always-on broadcasts."
			mobile-inline
			:usage="scheduleHeaderUsage">
			<Tooltip :active="cannotManageStreams" wrap-trigger :hide="1000">
				<FormButton class="gap-1" size="md" :disabled="cannotManageStreams" @click="showCreateStreamModal">
					<icon-base icon-name="plus" classIcon="mr-2"><icon-plus /></icon-base>
					New <span class="hidden md:inline">pre-recorded stream</span>
				</FormButton>
				<template #content>
					<p class="text-surface-8 text-sm" v-html="disabledTitle" />
				</template>
			</Tooltip>
		</PageHeader>
		<Spinner v-if="loadingStatus || !userloaded" text="Loading your pre-recorded streams…" classes="text-dark-8 mh-60"
			spinner-color="var(--c-dark-8)" spinner-size="15px" />
		<div v-else-if="!noAccess">
			<section class="mt-5">
				<ResponsiveToolbar v-if="scheduledStreams.length" class="my-3">
					<template v-if="showListControls">
						<DropDownMenu ref="sortDropdown" type="secondary" position="left">
							<template #toggle-button>
								<span class="text-surface-8">Sort by:&nbsp;</span>
								<span class="text-white">{{ sortByLabel }}</span>
								<icon-base class="!ml-1 shrink-0 text-surface-8">
									<icon-chevron-up-down />
								</icon-base>
							</template>
							<template #menu-items>
								<DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
									Oldest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
									Newest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
									A to Z
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
									Z to A
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
							</template>
						</DropDownMenu>
						<ListingSearchInput v-model="searchWidgetInput" />
					</template>
					<ViewModeSwitcher class="shrink-0" />
				</ResponsiveToolbar>
			</section>
			<Alert v-if="showBanner">
				<div>
					<h5 class="text-surface-8 text-sm font-medium">You're using {{ liveCounter }} streams, but your plan only
						allows {{ maxScheduleNumber }}.</h5>
					<p class="mb-0 text-surface-7 text-tiny font-normal">Please remove extra streams within {{ warningDays }}
						days, otherwise we'll need to disable them. To use {{ liveCounter }} streams, consider <a
							href="/app/subscribe">upgrading</a> your plan. <br>Thanks for your cooperation!</p>
				</div>
			</Alert>
			<section>
				<ul v-if="!loadingStatus" class="grid auto-rows-fr"
					:class="shouldShowCompactModeStreams ? '!grid-cols-1 gap-1.5' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4'">
					<li v-for="stream in streamFilter" :key="stream._id">
						<stream-card-view :stream="stream" :compact-mode="shouldShowCompactModeStreams"
							:notAllowed="streamFilterenabled.length >= maxScheduleNumber" @delete-stream="onStreamDeleteRequest"
							@pulse-update="updateStreamPulse" :hasEnabledLinkedSources="hasEnabledLinkedSources(stream)"
							:tooltipLinkedSourceText="getTooltipLinkedSourceText(stream)" />
					</li>
					<li v-if="!searchWidgetInput && scheduledStreams.length && canCreateScheduledStream">
						<ListingCreateTile
							label="pre-recorded stream"
							:view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
							:used="scheduledStreams.length"
							:limit="scheduleLimit"
							:limit-reached="atScheduledLimit"
							@create="showCreateStreamModal"
						/>
					</li>
				</ul>
				<ListingSearchEmpty v-if="noSearchMatches" :query="searchWidgetInput" noun="pre-recorded streams" @clear="searchWidgetInput = ''" />
				<EmptyState v-else-if="!streamFilter.length" title="Schedule your first pre-recorded stream"
					subtitle="Broadcast your uploaded videos on a schedule to run a 24/7 channel, with no encoder or manual go-live.">
					<template #illustration>
						<img src="@/assets/images/scheduler-plan.svg" alt="" width="104" height="104" />
					</template>
					<template #actions>
						<div class="flex flex-col w-[240px] mx-auto gap-2">
							<FormButton :disabled="cannotManageStreams" @click="showCreateStreamModal" class="!justify-center">
								<icon-base class="mr-2">
									<icon-plus />
								</icon-base>
								New pre-recorded stream
							</FormButton>
							<FormButton type="secondary" @click="showTutorialModal" class="!justify-center">
								<icon-base class="mr-2">
									<icon-play />
								</icon-base>
								Watch demo
							</FormButton>
						</div>
					</template>
				</EmptyState>
			</section>
		</div>
		<div class="no-access mt-5" v-else>
			<div class="title flex justify-between items-center">
				<h2 class="page-title mb-3">Pre-Recorded Streams</h2>
			</div>
			<div class="text-center pt-5 mt-5">
				<h4 class="text-dark-9 text-s-xl text-500 mb-1">You don’t have permission to access this.</h4>
				<p class="text-dark-6 text-s-m">Contact the team owner at <a class="text-dark-6 text-s-m text-underline"
						:href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
			</div>
		</div>
		<add-schedule-modal @new-channel="onNewStream" />
		<confirm-modal modal-type="danger" message="Would you like to delete this stream and all of its content?"
			@modal-confirm="onStreamDeleteConfirm" />
		<confirm-modal message="Please update your subscription to get more concurrent streams." modal-id="increase-limit"
			modal-type="danger" ok-text="Yes" cancel-text="Cancel" @modal-confirm="gotoUpgrade" />
		<!-- A paused or removed team membership is announced once, by the sidebar's
		access modal; a second copy here (same modal id) only stacked behind it. -->
		<demo-video-modal modalId="modal_prerecorded_tutorial" videoid="cmv93howf0pfuqmy887rqqve2" />
		<SidebarAddStream ref="sidebarAddStream" stream-type="scheduled" />
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
